import { normalizePublicError } from './error';

export type EmailPreferencePurpose = 'unsubscribe' | 'resubscribe';
export type MonitoringEmailStatus = 'subscribed' | 'unsubscribed';

export interface EmailPreferenceDetails {
  purpose: EmailPreferencePurpose;
  organizationName: string;
  maskedEmail: string;
  status: MonitoringEmailStatus;
  canConfirm: boolean;
}

export interface EmailPreferenceConfirmation {
  status: MonitoringEmailStatus;
  changed: boolean;
}

export class EmailPreferenceRequestError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'EmailPreferenceRequestError';
  }
}

function isEmailPreferenceDetails(value: unknown): value is EmailPreferenceDetails {
  if (typeof value !== 'object' || value === null) return false;

  const data = value as Record<string, unknown>;
  return (
    (data.purpose === 'unsubscribe' || data.purpose === 'resubscribe') &&
    typeof data.organizationName === 'string' &&
    typeof data.maskedEmail === 'string' &&
    (data.status === 'subscribed' || data.status === 'unsubscribed') &&
    typeof data.canConfirm === 'boolean'
  );
}

function isEmailPreferenceConfirmation(value: unknown): value is EmailPreferenceConfirmation {
  if (typeof value !== 'object' || value === null) return false;

  const data = value as Record<string, unknown>;
  return (
    (data.status === 'subscribed' || data.status === 'unsubscribed') &&
    typeof data.changed === 'boolean'
  );
}

async function readData<T>(
  response: Response,
  isExpected: (value: unknown) => value is T,
  fallbackMessage: string,
): Promise<T> {
  if (!response.ok) {
    try {
      const error = normalizePublicError(await response.json(), fallbackMessage);
      throw new EmailPreferenceRequestError(error.message, error.error);
    } catch (error) {
      if (error instanceof EmailPreferenceRequestError) throw error;
      throw new EmailPreferenceRequestError(fallbackMessage, 'REQUEST_FAILED');
    }
  }

  try {
    const payload = (await response.json()) as { data?: unknown };
    if (isExpected(payload.data)) return payload.data;
  } catch {
    // Use the route-specific stable error below.
  }

  throw new Error(fallbackMessage);
}

export const emailPreferencesApi = {
  async get(purpose: EmailPreferencePurpose, token: string): Promise<EmailPreferenceDetails> {
    const response = await fetch(`/api/email-preferences/${purpose}/${encodeURIComponent(token)}`, {
      credentials: 'omit',
    });

    return readData(
      response,
      isEmailPreferenceDetails,
      'Unable to load this email preference link.',
    );
  },

  async confirm(
    purpose: EmailPreferencePurpose,
    token: string,
  ): Promise<EmailPreferenceConfirmation> {
    const response = await fetch(`/api/email-preferences/${purpose}/${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({}),
    });

    return readData(
      response,
      isEmailPreferenceConfirmation,
      'Unable to update this email preference. Please try again.',
    );
  },
};
